import api, { ApiResponse } from './index'
import { User } from '@/types/auth'

export interface ProfileStats {
    totalGigs: number
    completedJobs: number
    totalEarnings: number
    averageRating: number
    responseTime: string
    successRate: number
}

export interface Review {
    _id: string
    rating: number
    comment: string
    reviewer: {
        name: string
        avatar?: string
    }
    date: string
    gigTitle: string
}

export interface ReviewsResponse {
    reviews: Review[]
    pagination: {
        currentPage: number
        totalPages: number
        totalCount: number
        pageSize: number
    }
    averageRating: number
    totalRatings: number
}

export interface GigsPagination {
    gigs: any[]
    pagination: {
        currentPage: number
        totalPages: number
        totalCount: number
        pageSize: number
    }
}

export const profileApi = {
    // Get user profile by ID
    getProfile: (userId: string): Promise<ApiResponse<{ user: User }>> =>
        api.get(`/users/${userId}`),

    // Get current user profile
    getCurrentProfile: (): Promise<ApiResponse<{ user: User }>> =>
        api.get('/users/me'),

    // Get user statistics
    getUserStats: (userId: string): Promise<ApiResponse<{ stats: ProfileStats }>> =>
        api.get(`/users/${userId}/stats`),

    // Get user's gigs/work history
    getUserGigs: (userId: string, params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<GigsPagination>> =>
        api.get(`/users/${userId}/gigs`, { params }),

    // Get user reviews
    getUserReviews: (userId: string, params: {
        page?: number
        limit?: number
        sortBy?: 'newest' | 'oldest' | 'rating'
    } = {}): Promise<ApiResponse<ReviewsResponse>> =>
        api.get(`/users/${userId}/reviews`, { params }),

    // Create a review
    createReview: (data: {
        gigId: string
        revieweeId: string
        rating: number
        comment: string
        tags?: string[]
    }): Promise<ApiResponse<{ review: any }>> =>
        api.post('/reviews', data),

    // Update a review
    updateReview: (reviewId: string, data: {
        rating?: number
        comment?: string
        tags?: string[]
    }): Promise<ApiResponse<{ review: any }>> =>
        api.put(`/reviews/${reviewId}`, data),

    // Delete a review
    deleteReview: (reviewId: string): Promise<ApiResponse> =>
        api.delete(`/reviews/${reviewId}`),

    // Report a review
    reportReview: (reviewId: string, data: {
        reason: string
        description?: string
    }): Promise<ApiResponse> =>
        api.post(`/reviews/${reviewId}/report`, data),

    // Mark review as helpful
    markReviewHelpful: (reviewId: string): Promise<ApiResponse<{ helpfulCount: number }>> =>
        api.post(`/reviews/${reviewId}/helpful`),

    // Record profile view
    recordProfileView: (profileOwnerId: string): Promise<ApiResponse> =>
        api.post(`/profile-views/record/${profileOwnerId}`),

    // Get profile view stats
    getProfileViewStats: (profileOwnerId: string, params: {
        timeRange?: 'week' | 'month' | 'year'
    } = {}): Promise<ApiResponse<any>> =>
        api.get(`/profile-views/stats/${profileOwnerId}`, { params }),

    // Get my profile view stats
    getMyProfileViewStats: (params: {
        timeRange?: 'week' | 'month' | 'year'
    } = {}): Promise<ApiResponse<any>> =>
        api.get('/profile-views/my-stats', { params })
}